import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import type { WorkspaceFolder, FolderColor } from '../../utils/workspaceFolders';
import { FOLDER_COLORS } from '../../utils/workspaceFolders';
import { getFolderById, getFolderPath, MAX_FOLDER_NAME_LENGTH } from '../../utils/folderTree';
import { pluralize } from '../../utils/workspaceLibrary';
import { Dialog } from './Dialog';
import { FolderTree } from './FolderTree';
import { FOLDER_COLOR_META } from './colors';
import type { DeleteFolderMode, DeleteFolderProgress, DeleteFolderResult } from './types';

const errorMessage = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);

/** What went wrong in a dialog, said the same way in each of them. */
const ErrorNote: React.FC<{ id?: string; className?: string; children: React.ReactNode }> = ({ id, className = '', children }) => (
    <p id={id} role="alert" className={`ns-note ${className}`} data-tone="danger">
        <AlertTriangle aria-hidden="true" /><span>{children}</span>
    </p>
);

// ── Shared picker (Location / Move) ─────────────────────────────────────────

interface PickerProps {
    folders: WorkspaceFolder[];
    counts?: Map<string, number>;
    value: string | null | undefined;
    onChange: (id: string | null) => void;
    rootLabel: string;
    rootCount?: number;
    disabledReason?: (id: string | null) => string | undefined;
    ariaLabel: string;
}

const FolderPicker: React.FC<PickerProps> = ({ folders, counts, value, onChange, rootLabel, rootCount, disabledReason, ariaLabel }) => {
    // Projects start expanded so the first level of folders is visible at a glance.
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set(folders.filter(f => f.kind === 'project').map(f => f.id)));
    useEffect(() => {
        // When the selection has ancestors, make sure they are open.
        if (!value) return;
        const path = getFolderPath(folders, value);
        if (path.length <= 1) return;
        setExpanded(prev => {
            const next = new Set(prev);
            path.slice(0, -1).forEach(f => next.add(f.id));
            return next;
        });
    }, [value, folders]);

    return (
        <div className="ns-well p-2 max-h-64 overflow-y-auto">
            <FolderTree
                folders={folders}
                counts={counts}
                selectedId={value === undefined ? '__none__' : value}
                onSelect={onChange}
                expandedIds={expanded}
                onToggleExpand={(id) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; })}
                mode="picker"
                rootLabel={rootLabel || undefined}
                rootCount={rootLabel ? rootCount : undefined}
                disabledReason={disabledReason}
                ariaLabel={ariaLabel}
            />
        </div>
    );
};

export const describeFolder = (folders: WorkspaceFolder[], id: string | null, rootLabel = 'Not in a project'): string => {
    if (!id) return rootLabel;
    const path = getFolderPath(folders, id);
    return path.length ? path.map(f => f.name).join(' / ') : rootLabel;
};

// ── Name dialog (create / rename, optional location) ────────────────────────

export interface LocationField {
    folders: WorkspaceFolder[];
    counts?: Map<string, number>;
    value: string | null;
    onChange: (id: string | null) => void;
    disabledReason?: (id: string | null) => string | undefined;
    rootLabel?: string;
}

export interface NameDialogProps {
    open: boolean;
    title: string;
    description?: React.ReactNode;
    label: string;
    placeholder?: string;
    initialValue?: string;
    submitLabel: string;
    onSubmit: (name: string) => Promise<void>;
    onClose: () => void;
    validate?: (name: string) => string | undefined;
    location?: LocationField;
    /** Optional colour swatches (projects). */
    color?: { value: FolderColor; onChange: (c: FolderColor) => void };
}

export const NameDialog: React.FC<NameDialogProps> = ({ open, title, description, label, placeholder, initialValue = '', submitLabel, onSubmit, onClose, validate, location, color }) => {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [showPicker, setShowPicker] = useState(false);

    useEffect(() => {
        if (open) {
            setValue(initialValue);
            setError(null);
            setBusy(false);
            setShowPicker(false);
        }
    }, [open, initialValue]);

    const submit = async () => {
        const name = value.replace(/\s+/g, ' ').trim();
        if (!name) { setError('Give it a name.'); return; }
        const problem = validate?.(name);
        if (problem) { setError(problem); return; }
        setBusy(true);
        setError(null);
        try {
            await onSubmit(name);
            onClose();
        } catch (e) {
            setError(errorMessage(e, 'Something went wrong. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            title={title}
            description={description}
            onClose={onClose}
            busy={busy}
            wide={!!location && showPicker}
            footer={
                <>
                    <button type="button" className="ns-btn ns-btn-soft ns-btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" className="ns-btn ns-btn-primary ns-btn-sm" onClick={submit} disabled={busy || !value.trim()}>
                        {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                        {submitLabel}
                    </button>
                </>
            }
        >
            <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="flex flex-col gap-4">
                <div>
                    <label className="ns-field-label" htmlFor="ns-lib-name-input">{label}</label>
                    <input
                        id="ns-lib-name-input"
                        data-autofocus
                        type="text"
                        className="ns-field"
                        value={value}
                        maxLength={MAX_FOLDER_NAME_LENGTH}
                        placeholder={placeholder}
                        onChange={(e) => { setValue(e.target.value); setError(null); }}
                        aria-invalid={!!error}
                        aria-describedby={error ? 'ns-lib-name-error' : undefined}
                        disabled={busy}
                        onFocus={(e) => e.target.select()}
                    />
                </div>

                {color && (
                    <div>
                        <div className="ns-field-label">Colour</div>
                        <ColorSwatches value={color.value} onChange={color.onChange} disabled={busy} />
                    </div>
                )}

                {location && (
                    <div>
                        <div className="ns-well flex items-center justify-between gap-3 pl-3 pr-2 py-2">
                            <div className="min-w-0">
                                <div className="ns-field-label mb-0.5">Location</div>
                                <div className="text-sm font-medium text-ink truncate">{describeFolder(location.folders, location.value, location.rootLabel)}</div>
                            </div>
                            {location.folders.length > 0 && (
                                <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm flex-none" onClick={() => setShowPicker(v => !v)} aria-expanded={showPicker} disabled={busy}>
                                    {showPicker ? 'Done' : 'Change'}
                                </button>
                            )}
                        </div>
                        {showPicker && (
                            <div className="mt-2">
                                <FolderPicker
                                    folders={location.folders}
                                    counts={location.counts}
                                    value={location.value}
                                    onChange={location.onChange}
                                    rootLabel={location.rootLabel || 'Not in a project'}
                                    disabledReason={location.disabledReason}
                                    ariaLabel="Choose a location"
                                />
                            </div>
                        )}
                    </div>
                )}

                {error && <ErrorNote id="ns-lib-name-error">{error}</ErrorNote>}
            </form>
        </Dialog>
    );
};

// ── Colour swatches ─────────────────────────────────────────────────────────

export const ColorSwatches: React.FC<{ value: FolderColor; onChange: (c: FolderColor) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
    <div role="radiogroup" aria-label="Colour" className="flex flex-wrap gap-2">
        {FOLDER_COLORS.map(c => {
            const meta = FOLDER_COLOR_META[c];
            const selected = c === value;
            return (
                <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={meta.label}
                    title={meta.label}
                    disabled={disabled}
                    onClick={() => onChange(c)}
                    className={`ns-round w-9 h-9 grid place-items-center border-2 transition-transform ${meta.swatch} ${selected ? 'border-ink scale-105' : 'border-transparent hover:scale-105'}`}
                >
                    {selected && <Check className={`w-4 h-4 ${c === 'ink' ? 'text-white' : 'text-ink'}`} aria-hidden="true" />}
                </button>
            );
        })}
    </div>
);

export interface ColorDialogProps {
    open: boolean;
    title: string;
    value: FolderColor;
    onSubmit: (c: FolderColor) => Promise<void>;
    onClose: () => void;
}

export const ColorDialog: React.FC<ColorDialogProps> = ({ open, title, value, onSubmit, onClose }) => {
    const [color, setColor] = useState<FolderColor>(value);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => { if (open) { setColor(value); setBusy(false); setError(null); } }, [open, value]);
    return (
        <Dialog
            open={open}
            title={title}
            onClose={onClose}
            busy={busy}
            footer={
                <>
                    <button type="button" className="ns-btn ns-btn-soft ns-btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
                    <button
                        type="button"
                        className="ns-btn ns-btn-primary ns-btn-sm"
                        disabled={busy}
                        onClick={async () => {
                            setBusy(true);
                            try { await onSubmit(color); onClose(); }
                            catch (e) { setError(errorMessage(e, 'Could not save the colour.')); }
                            finally { setBusy(false); }
                        }}
                    >
                        Save
                    </button>
                </>
            }
        >
            <ColorSwatches value={color} onChange={setColor} disabled={busy} />
            {error && <ErrorNote className="mt-3">{error}</ErrorNote>}
        </Dialog>
    );
};

// ── Move dialog ─────────────────────────────────────────────────────────────

export interface MoveDialogProps {
    open: boolean;
    title: string;
    description?: React.ReactNode;
    folders: WorkspaceFolder[];
    counts?: Map<string, number>;
    /** Show "Not in a project" as a destination. */
    allowRoot: boolean;
    rootCount?: number;
    disabledReason?: (id: string | null) => string | undefined;
    onMove: (targetId: string | null) => Promise<void>;
    onClose: () => void;
}

export const MoveDialog: React.FC<MoveDialogProps> = ({ open, title, description, folders, counts, allowRoot, rootCount, disabledReason, onMove, onClose }) => {
    const [target, setTarget] = useState<string | null | undefined>(undefined);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => { if (open) { setTarget(undefined); setBusy(false); setError(null); } }, [open]);

    const targetLabel = target === undefined ? null : describeFolder(folders, target);
    const hasProjects = folders.length > 0;

    return (
        <Dialog
            open={open}
            title={title}
            description={description}
            onClose={onClose}
            busy={busy}
            wide
            footer={
                <>
                    <button type="button" className="ns-btn ns-btn-soft ns-btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
                    <button
                        type="button"
                        className="ns-btn ns-btn-primary ns-btn-sm"
                        disabled={busy || target === undefined}
                        onClick={async () => {
                            if (target === undefined) return;
                            setBusy(true);
                            setError(null);
                            try { await onMove(target); onClose(); }
                            catch (e) { setError(errorMessage(e, 'Could not move. Please try again.')); }
                            finally { setBusy(false); }
                        }}
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                        Move here
                    </button>
                </>
            }
        >
            {hasProjects || allowRoot ? (
                <FolderPicker
                    folders={folders}
                    counts={counts}
                    value={target}
                    onChange={setTarget}
                    rootLabel={allowRoot ? 'Not in a project' : ''}
                    rootCount={allowRoot ? rootCount : undefined}
                    disabledReason={disabledReason}
                    ariaLabel="Choose a destination"
                />
            ) : (
                <p className="text-sm text-ink-soft">Create a project first, then you can move things into it.</p>
            )}
            <p className="text-sm text-ink-soft truncate mt-3" aria-live="polite">
                {targetLabel ? <>Destination: <span className="text-ink font-medium">{targetLabel}</span></> : 'Choose a destination above.'}
            </p>
            {error && <ErrorNote className="mt-3">{error}</ErrorNote>}
        </Dialog>
    );
};

// ── Delete folder / project ─────────────────────────────────────────────────

export interface DeleteFolderDialogProps {
    open: boolean;
    folder: WorkspaceFolder | null;
    folders: WorkspaceFolder[];
    descendantFolderCount: number;
    workspaceCount: number;
    onConfirm: (mode: DeleteFolderMode, onProgress: (p: DeleteFolderProgress) => void) => Promise<DeleteFolderResult>;
    onClose: () => void;
}

export const DeleteFolderDialog: React.FC<DeleteFolderDialogProps> = ({ open, folder, folders, descendantFolderCount, workspaceCount, onConfirm, onClose }) => {
    const [mode, setMode] = useState<DeleteFolderMode>('keep');
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState<DeleteFolderProgress | null>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => { if (open) { setMode('keep'); setAck(false); setBusy(false); setProgress(null); setError(null); } }, [open]);

    const kindLabel = folder?.kind === 'project' ? 'project' : 'folder';
    const destination = useMemo(() => {
        if (!folder) return 'Not in a project';
        const parent = getFolderById(folders, folder.parentId);
        return parent ? parent.name : 'Not in a project';
    }, [folder, folders]);
    const isEmpty = workspaceCount === 0 && descendantFolderCount === 0;
    const canConfirm = !busy && (mode === 'keep' || ack);

    const confirm = async () => {
        setBusy(true);
        setError(null);
        try {
            await onConfirm(mode, setProgress);
            onClose();
        } catch (e) {
            setError(errorMessage(e, 'Could not delete. Please try again.'));
            setBusy(false);
        }
    };

    if (!folder) return null;

    return (
        <Dialog
            open={open}
            title={`Delete “${folder.name}”?`}
            description={isEmpty ? `This ${kindLabel} is empty. Deleting it cannot be undone.` : undefined}
            onClose={onClose}
            busy={busy}
            footer={
                <>
                    <button type="button" className="ns-btn ns-btn-soft ns-btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
                    <button
                        type="button"
                        className={`ns-btn ns-btn-sm ${mode === 'all' && !isEmpty ? 'ns-btn-danger' : 'ns-btn-primary'}`}
                        onClick={confirm}
                        disabled={!canConfirm}
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                        {isEmpty ? `Delete ${kindLabel}` : mode === 'keep' ? `Delete ${kindLabel}, keep workspaces` : 'Delete everything'}
                    </button>
                </>
            }
        >
            {!isEmpty && (
                <div role="radiogroup" aria-label="What happens to its contents" className="flex flex-col gap-3">
                    <button type="button" role="radio" aria-checked={mode === 'keep'} className="ns-lib-option" onClick={() => setMode('keep')} disabled={busy}>
                        <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-none grid place-items-center ${mode === 'keep' ? 'border-lavender-deep' : 'border-cream-300'}`} aria-hidden="true">
                            {mode === 'keep' && <span className="w-2 h-2 rounded-full shrink-0 bg-lavender-deep" />}
                        </span>
                        <span>
                            <span className="block text-sm font-semibold">Keep the workspaces</span>
                            <span className="block text-sm text-ink-soft mt-0.5">
                                {pluralize(workspaceCount, 'workspace')} move{workspaceCount === 1 ? 's' : ''} to “{destination}”.
                                {descendantFolderCount > 0 && ` ${pluralize(descendantFolderCount, 'sub-folder')} ${descendantFolderCount === 1 ? 'is' : 'are'} removed.`}
                            </span>
                        </span>
                    </button>
                    <button type="button" role="radio" aria-checked={mode === 'all'} data-danger="true" className="ns-lib-option" onClick={() => setMode('all')} disabled={busy}>
                        <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-none grid place-items-center ${mode === 'all' ? 'border-danger-deep' : 'border-cream-300'}`} aria-hidden="true">
                            {mode === 'all' && <span className="w-2 h-2 rounded-full shrink-0 bg-danger-deep" />}
                        </span>
                        <span>
                            <span className="block text-sm font-semibold">Delete everything</span>
                            <span className="block text-sm text-ink-soft mt-0.5">
                                Permanently deletes {pluralize(workspaceCount, 'workspace')}
                                {descendantFolderCount > 0 && ` and ${pluralize(descendantFolderCount, 'sub-folder')}`}. This cannot be undone.
                            </span>
                        </span>
                    </button>
                    {mode === 'all' && (
                        // A true checkbox (it confirms, it is not a setting), drawn in the family: the design system has no part for one yet
                        <label className="flex items-start gap-3 text-sm text-ink px-1 cursor-pointer">
                            <span className="relative mt-px w-[18px] h-[18px] flex-none grid place-items-center">
                                <input
                                    type="checkbox"
                                    className="peer absolute inset-0 m-0 appearance-none rounded-sm border border-ink-faint bg-white cursor-pointer transition-colors checked:border-danger-deep checked:bg-danger-deep disabled:opacity-50 disabled:cursor-not-allowed"
                                    checked={ack}
                                    onChange={(e) => setAck(e.target.checked)}
                                    disabled={busy}
                                />
                                <Check className="relative w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" aria-hidden="true" />
                            </span>
                            <span>I understand these workspaces will be gone for good.</span>
                        </label>
                    )}
                </div>
            )}
            {progress && (
                <p role="status" className="text-sm text-ink-soft mt-4 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    {mode === 'all' ? 'Deleting' : 'Moving'} {progress.done} of {progress.total}…
                </p>
            )}
            {error && <ErrorNote className="mt-3">{error}</ErrorNote>}
        </Dialog>
    );
};

// ── Generic confirm ─────────────────────────────────────────────────────────

export interface ConfirmDialogProps {
    open: boolean;
    title: string;
    description: React.ReactNode;
    confirmLabel: string;
    danger?: boolean;
    onConfirm: () => Promise<void>;
    onClose: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ open, title, description, confirmLabel, danger, onConfirm, onClose }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => { if (open) { setBusy(false); setError(null); } }, [open]);
    return (
        <Dialog
            open={open}
            title={title}
            description={description}
            onClose={onClose}
            busy={busy}
            footer={
                <>
                    <button type="button" className="ns-btn ns-btn-soft ns-btn-sm" onClick={onClose} disabled={busy} data-autofocus>Cancel</button>
                    <button
                        type="button"
                        className={`ns-btn ns-btn-sm ${danger ? 'ns-btn-danger' : 'ns-btn-primary'}`}
                        disabled={busy}
                        onClick={async () => {
                            setBusy(true);
                            setError(null);
                            try { await onConfirm(); onClose(); }
                            catch (e) { setError(errorMessage(e, 'Something went wrong. Please try again.')); setBusy(false); }
                        }}
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                        {confirmLabel}
                    </button>
                </>
            }
        >
            {error && <ErrorNote>{error}</ErrorNote>}
        </Dialog>
    );
};
